import React, { useState, useEffect } from 'react';
// Added Grid hook
import { Row, Col, Card, Progress, Statistic, Avatar, Typography, message, Tag, Alert, Button, Divider, Grid } from 'antd';
import { RocketOutlined, TrophyOutlined, UserOutlined, BookOutlined, HourglassOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import api from '../../api/axios';

const { Title, Text, Paragraph } = Typography;
const { useBreakpoint } = Grid; // RESPONSIVE CHANGE: Hook to detect screen size

const MenteeDashboard = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const screens = useBreakpoint(); // RESPONSIVE CHANGE

    useEffect(() => {
        const fetchDashboard = async () => {
            try {
                const res = await api.get('/mentee/dashboard');
                setData(res.data);
            } catch (e) {
                message.error("Failed to load dashboard");
            } finally {
                setLoading(false);
            }
        };
        fetchDashboard();
    }, []);

    if (loading) return <Card loading={true} style={{ margin: '20px' }} />;

    const isMatched = data?.matched;

    return (
        // RESPONSIVE CHANGE: Reduce padding on mobile (xs)
        <div style={{ padding: screens.xs ? '10px' : '20px' }}>
            {!isMatched && (
                <Alert
                    message="Profile Under Review"
                    description={screens.xs ? "Admin is reviewing your profile." : "You have successfully set your goals! Our administrators are currently reviewing your profile..."}
                    type="info"
                    showIcon
                    icon={<HourglassOutlined />}
                    style={{ marginBottom: '24px', borderRadius: '8px' }}
                    // RESPONSIVE CHANGE: Hide action button on very small screens or make it fit
                    action={
                        <Button size="small" type="link" onClick={() => navigate('/mentee/commitments')}>
                            Goals
                        </Button>
                    }
                />
            )}

            <Title level={screens.xs ? 4 : 2}>
                {isMatched ? `Welcome Back, ${data?.mentor?.name || ''}` : 'Your Growth Journey'}
            </Title>
            
            <Row gutter={[16, 16]}> {/* RESPONSIVE CHANGE: Smaller gutter for mobile */}
                {/* Left Side: Stats & Progress */}
                <Col xs={24} lg={16}>
                    <Row gutter={[16, 16]}>
                        {/* RESPONSIVE CHANGE: Stack statistics vertically on mobile (xs={24}) */}
                        <Col xs={24} sm={12}>
                            <Card bordered={false} hoverable>
                                <Statistic 
                                    title="Active Goals" 
                                    value={data?.stats.total_commitments} 
                                    prefix={<BookOutlined style={{ color: '#FE1F08' }} />} 
                                />
                            </Card>
                        </Col>
                        <Col xs={24} sm={12}>
                            <Card bordered={false} hoverable>
                                <Statistic 
                                    title="Milestones" 
                                    value={data?.stats.completed_milestones} 
                                    suffix={`/ ${data?.stats.total_milestones}`}
                                    prefix={<TrophyOutlined style={{ color: '#FAAD14' }} />} 
                                />
                            </Card>
                        </Col>
                        <Col span={24}>
                            <Card title="Your Overall Growth Path">
                                <div style={{ textAlign: 'center', padding: screens.xs ? '10px' : '20px' }}>
                                    <Progress 
                                        type="dashboard" 
                                        percent={data?.stats.progress_percentage} 
                                        strokeColor={isMatched ? "#52c41a" : "#FE1F08"} 
                                        // RESPONSIVE CHANGE: Scale down the progress circle on mobile
                                        size={screens.xs ? 150 : 200} 
                                    />
                                    <div style={{ marginTop: 10 }}>
                                        <Text strong>
                                            {isMatched ? 'Path to Completion' : 'Goal Setting Progress'}
                                        </Text>
                                    </div>
                                </div>
                            </Card>
                        </Col>
                    </Row>
                </Col>

                {/* Right Side: Mentor Information */}
                {/* RESPONSIVE CHANGE: This will now naturally move below the stats on mobile */}
                <Col xs={24} lg={8}>
                    <Card 
                        title={isMatched ? "Your Mentor" : "Mentorship Status"} 
                        bordered={false} 
                        style={{ height: '100%', borderRadius: '12px' }}
                    >
                        {isMatched ? (
                            <div style={{ textAlign: 'center' }}>
                                <Avatar size={screens.xs ? 80 : 100} icon={<UserOutlined />} style={{ backgroundColor: '#FE1F08' }} />
                                <Title level={4} style={{ marginTop: 16 }}>{data.mentor.title} {data.mentor.name}</Title>
                                <Tag color="blue">{data.mentor.profession}</Tag>
                                <Divider />
                                <Paragraph italic>"Keep updating your progress!"</Paragraph>
                            </div>
                        ) : (
                            <div style={{ textAlign: 'center', padding: '10px 0' }}>
                                <Avatar size={60} icon={<RocketOutlined />} style={{ backgroundColor: '#f5f5f5', color: '#FE1F08' }} />
                                <Title level={5}>Pending Assignment</Title>
                                <Divider />
                                <Button type="dashed" block onClick={() => navigate('/mentee/commitments')}>
                                    Add More Goals
                                </Button>
                            </div>
                        )}
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default MenteeDashboard;